<script setup lang="ts">
import { computed } from "vue";
import type { MenuItemInterface } from "~~/server/types/menu";

const props = defineProps<{
  item: MenuItemInterface;
  idx: number;
}>();

const products = computed(() => (props.item.children || [])
  .filter(product => product.name?.trim() && product.url?.trim())
  .slice(0, 4));
</script>

<template>
  <div :id="`panel-${idx}`" class="panel panel-full panel--mega_with_content1" role="menu">
    <div class="panel-header">
      <div class="panel-title">{{ item.name }}</div>
      <NuxtLinkLocale :to="item.url" class="panel-view_all">
        {{ $t('view_all') }} <span aria-hidden="true">→</span>
      </NuxtLinkLocale>
    </div>

    <div class="panel-items">
      <NuxtLinkLocale
        v-for="product in products"
        :key="product.url"
        :to="product.url"
        class="p-card"
      >
        <div class="p-thumb">
          <img
            v-if="product.photo"
            :src="product.photo"
            class="p-img"
            loading="lazy"
            decoding="async"
            :alt="product.name"
          >
        </div>
        <div class="p-name">{{ product.name }}</div>
        <p v-if="product.short_description" class="p-description">{{ product.short_description }}</p>
      </NuxtLinkLocale>
    </div>
  </div>
</template>

<style scoped lang="scss">
.panel.panel-full.panel--mega_with_content1 {
  padding: 20px 24px 36px;
  white-space: normal;
  border: 1px solid var(--nav-bg);
  border-top: 0;
  max-height: calc(100vh - 170px);
  overflow: auto;

  .panel-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    margin: 0 0 36px;
    text-transform: uppercase;
    font-size: 1rem;
    font-weight: 600;
  }

  .panel-view_all {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    font-size: 0.875rem;
    color: inherit;
    text-decoration: none;

    span {
      font-size: 1.25rem;
      line-height: 1;
    }
  }

  .panel-items {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }

  .p-card {
    min-width: 0;
    color: inherit;
    text-align: center;
    text-decoration: none;
  }

  .p-thumb {
    aspect-ratio: 15 / 8;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.03);
  }

  .p-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .p-name {
    margin-top: 14px;
    font-size: 1rem;
    font-weight: 500;
    color: var(--el-color-primary);
    line-height: 1.25;
    text-transform: uppercase;
    overflow-wrap: anywhere;
  }

  .p-description {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin: 12px 0 0;
    font-size: 0.875rem;
    color: var(--body-color);
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .p-card,
  .panel-view_all {
    &:hover,
    &:focus-visible {
      color: var(--nav-panel-text-hover);
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 4px;
    }
  }
}
</style>
